// Option « tarification avancée » (activée par l'éditeur) : onglets « Prix à surveiller » et « Conditions fournisseurs » du
// catalogue de prix, remises négociées proposées à l'import. Rien de tout cela ne s'affiche quand l'option est inactive.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, Eye, EyeOff, Plus, Settings2, Truck } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { date, dateHeure, pluriel } from '../../lib/format.ts';
import { dateDuJourParis } from '@shared/dates.ts';
import { Alerte, Badge, Bouton, Champ, ChampChoix, Chargement, Dialogue, EtatErreur, EtatVide, Panneau, useNotifier } from '../../components/ui.tsx';
import { Montant, variationFr, versSaisie } from './commun.tsx';

// ─── Prix à surveiller ──────────────────────────────────────────────────────────────────────────
interface LigneFournisseur {
  cle: string; supplier_id: string | null; fournisseur: string | null; origine: string | null; price_item_id: string | null; prix: string | null; date: string | null;
  prix_precedent: string | null; date_precedente: string | null; variation_pct: string | null; age_jours: number | null; a_mettre_a_jour: boolean; nb_versions: number;
}
interface Article {
  reference: string; label: string | null; unit: string | null; famille: string | null; famille_libelle: string | null; surveille_par: 'reference' | 'famille';
  surveillance_id: string | null; fournisseurs: LigneFournisseur[]; a_mettre_a_jour: boolean;
}
interface Reglages { rappelJours: number; seuilVariationPct: string; validiteConseilleeJours: number }
interface EcranSurveillance {
  reglages: Reglages; defauts: Reglages; aujourdhui: string;
  surveilles: { id: string; reference: string | null; famille: string | null; famille_libelle: string | null; par: string | null; le: string }[];
  familles: { code: string; libelle: string }[]; articles: Article[]; nb_a_mettre_a_jour: number; droits: { ecrire: boolean; regler: boolean };
}

export function OngletSurveillance() {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const q = useQuery({ queryKey: ['surveillance'], queryFn: () => api.get<EcranSurveillance>('/api/tarification/surveillance') });
  const [reference, setReference] = useState('');
  const [famille, setFamille] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [reglages, setReglages] = useState(false);
  const rafraichir = () => Promise.all([qc.invalidateQueries({ queryKey: ['surveillance'] }), qc.invalidateQueries({ queryKey: ['prix'] }),
    qc.invalidateQueries({ queryKey: ['revision'] })]);
  const ajouter = async (corps: Record<string, string>) => {
    setErreur(null);
    try { await api.post('/api/tarification/surveillance', corps); setReference(''); setFamille(''); notifier('Article ajouté aux prix à surveiller.'); await rafraichir(); }
    catch (e) { setErreur(messageErreur(e)); }
  };
  const retirer = async (id: string) => {
    try { await api.del(`/api/tarification/surveillance/${id}`); notifier('Surveillance retirée.'); await rafraichir(); } catch (e) { notifier(messageErreur(e), true); }
  };
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const v = q.data!;
  const familles = v.surveilles.filter((s) => s.famille);
  return (
    <div className="pile-l">
      <Panneau titre="Prix à surveiller" actions={v.droits.regler && <Bouton compact icone={<Settings2 aria-hidden />} onClick={() => setReglages(true)}>Réglages</Bouton>}>
        <div className="pile">
          <p className="petit secondaire">{`Articles au prix volatil (cuivre, fluide, câble…) : dernier prix de chaque fournisseur, variation depuis le prix précédent,
            saisie rapide du prix du mois (nouvelle version datée, l’ancienne est conservée). Rappel : article non mis à jour depuis plus de ${v.reglages.rappelJours} jours
            (valeur par défaut : ${v.defauts.rappelJours} jours).`}</p>
          {v.nb_a_mettre_a_jour > 0 && <Alerte ton="ambre" titre={`${pluriel(v.nb_a_mettre_a_jour, 'article surveillé', 'articles surveillés')} à mettre à jour`}>
            {`Dernier prix daté de plus de ${v.reglages.rappelJours} jours.`}</Alerte>}
          {v.droits.ecrire && (
            <div className="grille-2 tarif-ajout-surveillance">
              <form className="rangee tarif-ajout" onSubmit={(e) => { e.preventDefault(); if (reference.trim()) void ajouter({ reference: reference.trim() }); }}>
                <Champ libelle="Surveiller une référence" mono value={reference} maxLength={120} onChange={(e) => setReference(e.target.value)} placeholder="Ex. CU-14-38" />
                <Bouton type="submit" icone={<Eye aria-hidden />} disabled={!reference.trim()}>Surveiller</Bouton>
              </form>
              <form className="rangee tarif-ajout" onSubmit={(e) => { e.preventDefault(); if (famille) void ajouter({ famille }); }}>
                <ChampChoix libelle="Surveiller une famille d’articles" vide="Choisir" value={famille} onChange={(e) => setFamille(e.target.value)}
                  options={v.familles.filter((f) => !familles.some((s) => s.famille === f.code)).map((f) => ({ valeur: f.code, libelle: f.libelle }))} />
                <Bouton type="submit" icone={<Eye aria-hidden />} disabled={!famille}>Surveiller</Bouton>
              </form>
            </div>
          )}
          {erreur && <Alerte ton="danger">{erreur}</Alerte>}
          {!!familles.length && (
            <div className="rangee tarif-familles" aria-label="Familles surveillées">
              {familles.map((f) => <span key={f.id} className="rangee" style={{ gap: 4 }}><Badge ton="info">{f.famille_libelle ?? f.famille}</Badge>
                {v.droits.ecrire && <Bouton compact variante="fantome" icone={<EyeOff aria-hidden />} aria-label={`Ne plus surveiller ${f.famille_libelle}`} onClick={() => retirer(f.id)}>Retirer</Bouton>}</span>)}
            </div>
          )}
        </div>
      </Panneau>
      {!v.articles.length && <EtatVide icone={<BellRing />} titre="Aucun article surveillé" texte="Ajoutez une référence ou une famille d’articles (cuivre, câble, fluide…)." />}
      <ul className="tarif-articles">
        {v.articles.map((a) => <CarteArticle key={a.reference} a={a} v={v} retirer={retirer} apres={rafraichir} />)}
      </ul>
      {reglages && <DialogueReglages v={v} fermer={() => setReglages(false)} apres={rafraichir} />}
    </div>
  );
}

function CarteArticle({ a, v, retirer, apres }: { a: Article; v: EcranSurveillance; retirer: (id: string) => void; apres: () => Promise<unknown> }) {
  return (
    <li className="panneau tarif-article">
      <div className="tarif-article-haut">
        <div className="pile" style={{ gap: 2, minWidth: 0 }}>
          <strong><span className="mono">{a.reference}</span>{a.label ? ` — ${a.label}` : ''}</strong>
          <span className="rangee" style={{ gap: 6 }}>
            {a.famille_libelle && <Badge>{a.famille_libelle}</Badge>}
            {a.surveille_par === 'famille' && <span className="tres-petit discret">surveillé par sa famille</span>}
            {a.a_mettre_a_jour && <Badge ton="ambre">À mettre à jour</Badge>}
          </span>
        </div>
        {v.droits.ecrire && a.surveillance_id && <Bouton compact variante="fantome" icone={<EyeOff aria-hidden />} onClick={() => retirer(a.surveillance_id!)}>Ne plus surveiller</Bouton>}
      </div>
      {!a.fournisseurs.length && <p className="petit discret">Aucun prix connu pour cette référence.</p>}
      <ul className="tarif-fournisseurs">
        {a.fournisseurs.map((f) => <LigneSurveillee key={f.cle} a={a} f={f} v={v} apres={apres} />)}
      </ul>
    </li>
  );
}

function LigneSurveillee({ a, f, v, apres }: { a: Article; f: LigneFournisseur; v: EcranSurveillance; apres: () => Promise<unknown> }) {
  const notifier = useNotifier();
  const [prix, setPrix] = useState('');
  const [jour, setJour] = useState(v.aujourdhui);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const enregistrer = async () => {
    setEnvoi(true); setErreur(null);
    try {
      await api.post('/api/tarification/prix-du-mois', { reference: a.reference, supplierId: f.supplier_id, prix, date: jour });
      notifier(`Prix du ${date(jour)} enregistré pour ${a.reference}${f.fournisseur ? ` (${f.fournisseur})` : ''}.`); setPrix('');
      await apres();
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <li className="tarif-fournisseur">
      <div className="tarif-fournisseur-infos">
        <strong className="petit">{f.fournisseur ?? (f.origine === 'saisie' ? 'Saisie sans fournisseur' : 'Sans fournisseur')}</strong>
        <span className="chiffre"><Montant v={f.prix} />{a.unit ? <span className="tres-petit discret">{` / ${a.unit}`}</span> : null}</span>
        <span className="tres-petit discret">{f.date ? `du ${date(f.date)}${f.age_jours === null ? '' : f.age_jours === 0 ? ' (aujourd’hui)' : ` (il y a ${pluriel(f.age_jours, 'jour')})`}` : 'date inconnue'}</span>
        {f.variation_pct !== null
          ? <Badge ton={Math.abs(Number(f.variation_pct)) >= Number(v.reglages.seuilVariationPct) ? 'ambre' : 'neutre'}>{`${variationFr(f.variation_pct)} depuis le ${f.date_precedente ? date(f.date_precedente) : 'prix précédent'}`}</Badge>
          : <span className="tres-petit discret">Pas de prix précédent</span>}
        {f.a_mettre_a_jour && <Badge ton="ambre">{`Plus de ${v.reglages.rappelJours} jours`}</Badge>}
      </div>
      {v.droits.ecrire && (
        <form className="tarif-saisie-rapide" onSubmit={(e) => { e.preventDefault(); void enregistrer(); }}>
          <Champ libelle="Prix du mois (€ HT)" inputMode="decimal" value={prix} onChange={(e) => setPrix(e.target.value)} placeholder={versSaisie(f.prix)} />
          <Champ libelle="Date" type="date" value={jour} max={v.aujourdhui} onChange={(e) => setJour(e.target.value)} />
          <Bouton type="submit" variante="principal" chargement={envoi} disabled={!prix.trim() || !jour}>Enregistrer</Bouton>
        </form>
      )}
      {erreur && <Alerte ton="danger">{erreur}</Alerte>}
    </li>
  );
}

function DialogueReglages({ v, fermer, apres }: { v: EcranSurveillance; fermer: () => void; apres: () => Promise<unknown> }) {
  const notifier = useNotifier();
  const [r, setR] = useState({ rappelJours: String(v.reglages.rappelJours), seuilVariationPct: versSaisie(v.reglages.seuilVariationPct), validiteConseilleeJours: String(v.reglages.validiteConseilleeJours) });
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const enregistrer = async () => {
    setEnvoi(true); setErreur(null);
    try {
      await api.put('/api/tarification/reglages', { rappelJours: Number(r.rappelJours), seuilVariationPct: r.seuilVariationPct, validiteConseilleeJours: Number(r.validiteConseilleeJours) });
      notifier('Réglages de la tarification enregistrés.'); await apres(); fermer();
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Réglages des prix à surveiller" ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} onClick={enregistrer}>Enregistrer</Bouton></>}>
      <div className="pile">
        <Champ libelle="Rappel : article non mis à jour depuis (jours)" inputMode="numeric" value={r.rappelJours} onChange={(e) => setR({ ...r, rappelJours: e.target.value })}
          aide={`Valeur par défaut : ${v.defauts.rappelJours} jours. De 1 à 365.`} />
        <Champ libelle="Seuil de variation signalée sur un devis (%)" inputMode="decimal" value={r.seuilVariationPct} onChange={(e) => setR({ ...r, seuilVariationPct: e.target.value })}
          aide={`Valeur par défaut : ${versSaisie(v.defauts.seuilVariationPct)} %. Hausse ou baisse d’un prix plus récent par rapport au prix du devis.`} />
        <Champ libelle="Validité conseillée d’un devis à prix volatils (jours)" inputMode="numeric" value={r.validiteConseilleeJours}
          onChange={(e) => setR({ ...r, validiteConseilleeJours: e.target.value })} aide={`Valeur par défaut : ${v.defauts.validiteConseilleeJours} jours. Un conseil seulement : rien n’est modifié.`} />
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
      </div>
    </Dialogue>
  );
}

// ─── Conditions fournisseurs : remises négociées, frais d'approche, franco ──────────────────────
interface Conditions {
  fournisseur: { id: string; name: string }; aujourdhui: string;
  remises: { fabricant: string | null; remise: string; depuis: string; par: string | null; note: string | null }[];
  frais: { approche_pct: string | null; franco_ht: string | null; frais_fixes_ht: string | null; depuis: string } | null;
  historique: { remises: { id: string; fabricant: string | null; remise: string | null; depuis: string; note: string | null; created_at: string; par: string | null }[];
    frais: { id: string; approche_pct: string | null; franco_ht: string | null; frais_fixes_ht: string | null; depuis: string; created_at: string; par: string | null }[] };
  fabricants: string[]; ecrire: boolean;
}

export function OngletConditions() {
  const f = useQuery({ queryKey: ['fournisseurs'], queryFn: () => api.get<{ id: string; name: string }[]>('/api/fournisseurs') });
  const [choix, setChoix] = useState('');
  const id = choix || f.data?.[0]?.id || '';
  return (
    <div className="pile-l">
      <Panneau titre="Conditions négociées par fournisseur">
        <div className="pile">
          <p className="petit secondaire">Remise par défaut et remises par fabricant, datées : elles sont proposées aux PROCHAINS imports de tarif brut de ce fournisseur
            (modifiables avant validation) et ne changent jamais un prix déjà importé. Frais d’approche : ajoutés au coût des lignes de devis qui reprennent un prix
            de ce fournisseur. Franco et frais de port : rappelés sur la liste du matériel à commander.</p>
          {f.isLoading && <Chargement lignes={1} />}
          {f.data && !f.data.length && <EtatVide icone={<Truck />} titre="Aucun fournisseur" texte="Importez d’abord le tarif d’un fournisseur." />}
          {!!f.data?.length && <ChampChoix libelle="Fournisseur" value={id} onChange={(e) => setChoix(e.target.value)} options={f.data.map((x) => ({ valeur: x.id, libelle: x.name }))} />}
        </div>
      </Panneau>
      {id && <ConditionsFournisseur id={id} />}
    </div>
  );
}

function ConditionsFournisseur({ id }: { id: string }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const q = useQuery({ queryKey: ['conditions', id], queryFn: () => api.get<Conditions>(`/api/fournisseurs/${id}/conditions`) });
  const [remise, setRemise] = useState({ fabricant: '', remise: '', dateEffet: dateDuJourParis(), note: '' });
  const [frais, setFrais] = useState<{ approchePct: string; francoHt: string; fraisFixesHt: string; dateEffet: string } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState<string | null>(null);
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const c = q.data!;
  const fr = frais ?? { approchePct: versSaisie(c.frais?.approche_pct), francoHt: versSaisie(c.frais?.franco_ht), fraisFixesHt: versSaisie(c.frais?.frais_fixes_ht), dateEffet: c.aujourdhui };
  const envoyer = async (cle: string, url: string, corps: unknown, message: string) => {
    setEnvoi(cle); setErreur(null);
    try { await api.post(url, corps); notifier(message); await qc.invalidateQueries({ queryKey: ['conditions', id] }); return true; }
    catch (e) { setErreur(messageErreur(e)); return false; } finally { setEnvoi(null); }
  };
  return (
    <div className="pile-l">
      <Panneau titre={`Remises négociées — ${c.fournisseur.name}`}>
        <div className="pile">
          {!c.remises.length && <p className="petit discret">Aucune remise négociée en vigueur.</p>}
          {!!c.remises.length && (
            <ul className="devis-cartes-catalogue">{c.remises.map((r) => (
              <li key={r.fabricant ?? ''} className="devis-carte-catalogue">
                <div className="pile"><strong>{r.fabricant ?? 'Remise par défaut'}</strong>
                  <span className="tres-petit discret">{`depuis le ${date(r.depuis)}${r.par ? ` — ${r.par}` : ''}${r.note ? ` — ${r.note}` : ''}`}</span></div>
                <div className="devis-carte-catalogue-droite"><span className="chiffre">{`${versSaisie(r.remise)} %`}</span>
                  {c.ecrire && <Bouton compact variante="fantome" chargement={envoi === `retirer-${r.fabricant}`}
                    onClick={() => envoyer(`retirer-${r.fabricant}`, `/api/fournisseurs/${id}/remises`, { fabricant: r.fabricant, remise: null, dateEffet: c.aujourdhui }, 'Remise retirée (historique conservé).')}>Retirer</Bouton>}</div>
              </li>))}</ul>
          )}
          {c.ecrire && (
            <form className="pile" onSubmit={async (e) => { e.preventDefault(); if (await envoyer('remise', `/api/fournisseurs/${id}/remises`,
              { fabricant: remise.fabricant || null, remise: remise.remise, dateEffet: remise.dateEffet, note: remise.note || null }, 'Remise négociée enregistrée.'))
              setRemise({ ...remise, fabricant: '', remise: '', note: '' }); }}>
              <div className="grille-3">
                <Champ libelle="Fabricant (vide = remise par défaut)" list={`fab-${id}`} value={remise.fabricant} maxLength={120} onChange={(e) => setRemise({ ...remise, fabricant: e.target.value })} />
                <datalist id={`fab-${id}`}>{c.fabricants.map((x) => <option key={x} value={x} />)}</datalist>
                <Champ libelle="Remise (%)" inputMode="decimal" value={remise.remise} onChange={(e) => setRemise({ ...remise, remise: e.target.value })} required />
                <Champ libelle="Date d’effet" type="date" value={remise.dateEffet} onChange={(e) => setRemise({ ...remise, dateEffet: e.target.value })} required />
              </div>
              <Champ libelle="Note (facultatif)" value={remise.note} maxLength={500} onChange={(e) => setRemise({ ...remise, note: e.target.value })} placeholder="Ex. accord commercial 2027" />
              <div className="groupe-btn"><Bouton type="submit" variante="principal" icone={<Plus aria-hidden />} chargement={envoi === 'remise'} disabled={!remise.remise.trim()}>Enregistrer la remise</Bouton></div>
            </form>
          )}
          {!!c.historique.remises.length && (
            <details className="details-44"><summary className="petit">Historique des remises</summary>
              <ul className="devis-liste-simple petit">{c.historique.remises.map((h) => (
                <li key={h.id}>{`${h.fabricant ?? 'Par défaut'} : ${h.remise === null ? 'retirée' : `${versSaisie(h.remise)} %`} à partir du ${date(h.depuis)} — saisi le ${dateHeure(h.created_at)}${h.par ? ` par ${h.par}` : ''}`}</li>))}</ul>
            </details>
          )}
        </div>
      </Panneau>
      <Panneau titre="Frais d’approche et franco">
        <div className="pile">
          {c.frais ? <p className="petit">{[c.frais.approche_pct ? `Frais d’approche ${versSaisie(c.frais.approche_pct)} %` : 'Aucun frais d’approche',
            c.frais.franco_ht ? `franco ${versSaisie(c.frais.franco_ht)} € HT` : null, c.frais.frais_fixes_ht ? `port ${versSaisie(c.frais.frais_fixes_ht)} € HT sous le franco` : null]
            .filter(Boolean).join(' — ')}{` (depuis le ${date(c.frais.depuis)})`}</p> : <p className="petit discret">Aucun frais enregistré.</p>}
          {c.ecrire && (
            <form className="pile" onSubmit={async (e) => { e.preventDefault(); if (await envoyer('frais', `/api/fournisseurs/${id}/frais`,
              { approchePct: fr.approchePct || null, francoHt: fr.francoHt || null, fraisFixesHt: fr.fraisFixesHt || null, dateEffet: fr.dateEffet }, 'Frais du fournisseur enregistrés.')) setFrais(null); }}>
              <div className="grille-2">
                <Champ libelle="Frais d’approche (% du prix d’achat)" inputMode="decimal" value={fr.approchePct} onChange={(e) => setFrais({ ...fr, approchePct: e.target.value })} aide="De 0 à 50 %. Vide = aucun." />
                <Champ libelle="Date d’effet" type="date" value={fr.dateEffet} onChange={(e) => setFrais({ ...fr, dateEffet: e.target.value })} required />
                <Champ libelle="Franco de port (€ HT)" inputMode="decimal" value={fr.francoHt} onChange={(e) => setFrais({ ...fr, francoHt: e.target.value })} />
                <Champ libelle="Frais de port sous le franco (€ HT par commande)" inputMode="decimal" value={fr.fraisFixesHt} onChange={(e) => setFrais({ ...fr, fraisFixesHt: e.target.value })} />
              </div>
              <div className="groupe-btn"><Bouton type="submit" variante="principal" chargement={envoi === 'frais'}>Enregistrer les frais</Bouton></div>
            </form>
          )}
        </div>
      </Panneau>
      {erreur && <Alerte ton="danger">{erreur}</Alerte>}
    </div>
  );
}

// ─── Import d'un tarif : remises négociées proposées, modifiables avant validation ──────────────
export interface RemiseNegociee { fabricant: string | null; remise: string; depuis: string }
export function RemisesImport({ remises, valeurs, changer }: { remises: RemiseNegociee[]; valeurs: { fabricant: string; remise: string }[];
  changer: (v: { fabricant: string; remise: string }[]) => void }) {
  const defaut = remises.find((r) => r.fabricant === null);
  return (
    <div className="pile tarif-remises-import">
      <strong className="petit">Remises négociées avec ce fournisseur (proposées)</strong>
      {!remises.length && <span className="petit discret">Aucune remise négociée en vigueur à la date du tarif.</span>}
      {defaut && <span className="petit">{`Remise par défaut ${versSaisie(defaut.remise)} % (depuis le ${date(defaut.depuis)}) : reprise dans la remise globale ci-dessus, modifiable.`}</span>}
      {valeurs.map((v, i) => (
        <div key={i} className="rangee tarif-ajout">
          <Champ libelle="Fabricant" value={v.fabricant} onChange={(e) => changer(valeurs.map((x, j) => (j === i ? { ...x, fabricant: e.target.value } : x)))} />
          <Champ libelle="Remise (%)" inputMode="decimal" value={v.remise} onChange={(e) => changer(valeurs.map((x, j) => (j === i ? { ...x, remise: e.target.value } : x)))} />
          <Bouton compact variante="fantome" onClick={() => changer(valeurs.filter((_, j) => j !== i))}>Retirer</Bouton>
        </div>
      ))}
      <div className="groupe-btn"><Bouton compact icone={<Plus aria-hidden />} onClick={() => changer([...valeurs, { fabricant: '', remise: '' }])}>Ajouter une remise par fabricant</Bouton></div>
      <span className="tres-petit discret">Ordre : remise écrite dans le fichier, sinon remise du fabricant, sinon remise globale. Les prix déjà importés ne changent pas.</span>
    </div>
  );
}
